import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, withCtx as _withCtx, createBlock as _createBlock, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, Fragment as _Fragment } from "vue"
import _imports_0 from '../../assets/back-arrow.svg'
import _imports_1 from '../../assets/arrow-icon.svg'


const _hoisted_1 = /*#__PURE__*/_createElementVNode("div", {
  id: "example",
  style: {"display":"none"}
}, null, -1)
const _hoisted_2 = {
  key: 0,
  class: "center-container"
}
const _hoisted_3 = /*#__PURE__*/_createElementVNode("div", { class: "mt-4 mb-2" }, [
  /*#__PURE__*/_createElementVNode("h2", null, "Receipt")
], -1)
const _hoisted_4 = {
  key: 0,
  class: "button-container"
}
const _hoisted_5 = /*#__PURE__*/_createElementVNode("img", {
  class: "ml-2",
  src: _imports_0,
  alt: "arrow-right"
}, null, -1)
const _hoisted_6 = {
  key: 0,
  class: "ml-2",
  src: _imports_1,
  alt: "arrow-right"
}

import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { VForm, VWindow } from "vuetify/lib/components/index.mjs";
import { useProviderStore } from "@/stores/Provider";
import { useSessionStore } from "@/stores/Session";
import useDocumentStore from "@/stores/Document";
import VgsForm from "@/types/VgsForm";
import Session from "@/types/Session";
import PatientInformationForm from "@/components/vision/PatientInformation.vue";
import InsuranceInformationForm from "@/components/vision/InsuranceInformation.vue";
import ClaimInformationForm from "@/components/vision/ClaimInformation.vue";
import ProgressBar from "@/components/common/ProgressBar.vue";
import Review from "@/components/vision/Review.vue";
import FinalReview from "@/components/common/FinalReview.vue";
import ClaimFiled from "@/components/common/ClaimFiled.vue";
import { getUserToken } from "@/utils/auth";
import VisionVariables from "@/types/VisionVariables";
import VisionFormSteps from "@/types/VisionFormSteps";
import VisionProvider from "@/types/VisionProvider";
import VisionClaimItems from "@/types/VisionClaimItems";
import VisionItem from "@/types/VisionItem";
import BinkeyLogo from "@/components/common/BinkeyLogo.vue";
import MyFile from "@/types/File";
import FileInput from "@/components/custom/FileInput.vue";
import * as rules from "@/utils/rules";
import {
  addSignatureToPdf,
  appendImageOrPdf,
  flattenPDFForm,
} from "@/utils/pdf";
import DeliveryType from "@/types/DeliveryType";
import { useBucketStore } from "@/stores/Bucket";
import { base64ToFile } from "@/utils/converters";


export default /*#__PURE__*/_defineComponent({
  __name: 'VisionFlow',
  setup(__props) {

const USE_VGS = process.env.VUE_APP_USE_VGS === "true";
const POSTGRID_SANDBOX = process.env.VUE_APP_POSTGRID_SANDBOX === "true";
const SHOW_CLAIM_PDFS = process.env.VUE_APP_SHOW_CLAIM_PDFS === "true";
const isInsuranceProviderNotListed = ref(false);

const sessionId = getUserToken();
const currentStep = ref<VisionFormSteps>(VisionFormSteps.PersonalInformation);
const shouldShowBackBtn = computed(
  () =>
    currentStep.value != VisionFormSteps.PersonalInformation &&
    !isInsuranceProviderNotListed.value
);

const shouldShowNextButton = computed(() => {
  if (isInsuranceProviderNotListed.value) {
    return false;
  } else {
    return true; // always render it
  }
});

// Refs to template DOM elements
const windowScreen = ref(null);
const personalInformation = ref(null);
const insuranceInformation = ref(null);
const claimInformation = ref(null);
const finalReviewForm = ref(null);
const receipt = new MyFile(`receipt-${sessionId}`, "");
const receiptLoaded = ref(false); // Add a reactive property
const sheetWidth = ref(calculateWidth());
const userPostgridContactId = ref<string>("");
const claimPdfKey = ref<string>("");
const documentStore = useDocumentStore();

const updateInsuranceStatus = (status: boolean) => {
  isInsuranceProviderNotListed.value = status;
};

function nextStep(): void {
  if (windowScreen.value) (windowScreen.value as VWindow).group.next();
}
function prevStep(): void {
  if (windowScreen.value) (windowScreen.value as VWindow).group.prev();
}

// To do: find better way to do this
function getRefToCurrentStep(): VForm | null {
  switch (currentStep.value) {
    case VisionFormSteps.PersonalInformation:
      return personalInformation.value;
    case VisionFormSteps.InsuranceInformation:
      return insuranceInformation.value;
    case VisionFormSteps.ClaimInformation:
      return claimInformation.value;
    case VisionFormSteps.FinalReview:
      return finalReviewForm.value;
    default:
      return null;
  }
}

async function validateCurrentStep() {
  const form = getRefToCurrentStep();
  return form ? (await form.validate()).valid : true;
}

const shouldShowErrorMessage = ref<boolean>(false);
const errorMessage = ref<string>("");

function showErrorMessage(message: string) {
  errorMessage.value = message;
  shouldShowErrorMessage.value = true;
}

const providers = ref<VisionProvider[]>([]);

// To do: find better way to do this
const selectedProvider = computed(() =>
  providers.value.find(
    (provider) => provider.name === variables.value.planAdministrator
  )
);

const deliveryType = computed(() => selectedProvider.value?.deliveryType);
const faxNumber = computed(
  () => process.env.VUE_APP_PDF_FAX || selectedProvider.value?.faxNumber
);

const postgridContactId = computed(() =>
  POSTGRID_SANDBOX
    ? selectedProvider.value?.postgridContactId?.sandbox
    : selectedProvider.value?.postgridContactId?.live
);

async function onClickNext() {
  const isValid = await validateCurrentStep();

  if (isValid) {
    if (currentStep.value == VisionFormSteps.FinalReview) {
      generatingPdfs.value = true;
      await generateClaimPdf();
      await saveSession();
      await submitClaim();
      generatingPdfs.value = false;
    }
    nextStep();

    await saveSession();
  } else {
    showErrorMessage("Please enter the required fields");
  }
}

const variables = ref<VisionVariables>({} as VisionVariables);

const vgsForm = ref<VgsForm | undefined>(undefined);
const claimPdfs = ref<string[]>([]);
const generatingPdfs = ref<boolean>(false);
const errorGeneratingPdfs = ref<boolean>(false);
const loadingSession = ref<boolean>(true);
const savingSession = ref<boolean>(false);
const sessionVal = ref<Session | null>(null);
// const invoices = ref<VisionInvoice[]>([]);
// const items = ref<VisionItem>({} as VisionItem);

async function loadSession() {
  const sessionStore = useSessionStore();

  const response = USE_VGS
    ? await sessionStore.getSessionFromVgs(sessionId)
    : await sessionStore.getSession(sessionId);

  const session = response?.data.session;

  sessionVal.value = session;

  // To do: change in back end
  if (session?.subject?.lastName)
    variables.value.lastName = session.subject.lastName;
  if (session?.subject?.firstName)
    variables.value.firstName = session.subject.firstName;
  if (session?.subject?.middleName)
    variables.value.middleInitial = session.subject.middleName;
  if (session?.subject?.state)
    variables.value.addressState = session.subject.state;
  if (session?.subject?.city)
    variables.value.addressCity = session.subject.city;
  if (session?.subject?.zip) variables.value.addressZip = session.subject.zip;
  if (session?.subject?.address)
    variables.value.addressStreet = session.subject.address;
  if (session?.subject?.email) variables.value.email = session.subject.email;
  if (session?.subject?.phone)
    variables.value.phoneNumber = session.subject.phone;
  if (session?.subject?.sex)
    variables.value.sex = session?.subject?.sex || session.claim.sex;

  if (session?.visionClaimItems) claimItems.value = session.visionClaimItems;

  if (session?.claim) variables.value = session.claim;
  if (session?.step) currentStep.value = session.step;

  await receipt.load(session?.receipt?.key, session?.receipt?.file_type);

  receiptLoaded.value = true;
  loadingSession.value = false;
}

async function saveSession() {
  savingSession.value = true;

  // To do: change in back end
  const contactIdOrFaxNumber =
    deliveryType.value == DeliveryType.Address
      ? postgridContactId.value
      : faxNumber.value;

  // To do: change in back end
  await receipt.save();
  const receiptInfo = await receipt.getInfo();
  const data = {
    sessionId: sessionId,
    step: currentStep.value,
    signature: signatureDataUrl.value,
    deliveryType: deliveryType.value,
    deliveryAddress: contactIdOrFaxNumber,
    contact: userPostgridContactId.value,
    providerName: variables.value.planAdministrator,
    receipt: receiptInfo,
    visionClaimItems: claimItems.value,
    pdfKeys: [claimPdfKey.value],
    variables: {
      ...variables.value,
      ...getItemsData(),
      serviceDate: getEarliestDate()?.dateString,
      fullName: fullName.value,
      accountHolderFullName: accountHolderFullName.value,
      accountHolderPhoneNumber: variables.value.accountHolderPhoneNumber
        ? variables.value.accountHolderPhoneNumber.replace(/[^\d]/g, "")
        : undefined,
      phoneNumber: variables.value.phoneNumber
        ? variables.value.phoneNumber.replace(/[^\d]/g, "")
        : undefined,
      hsl_review_answers: true,
    },
  };

  try {
    if (USE_VGS) {
      await submitVgsForm(JSON.parse(JSON.stringify(data)));
    } else {
      await useSessionStore().saveSession(data);
    }
  } catch {
    showErrorMessage("Error saving session information");
  } finally {
    savingSession.value = false;
  }
}

async function submitVgsForm(data: Session) {
  const apiKey = process.env.VUE_APP_BACKEND_API_KEY as string;

  vgsForm.value?.toRaw().submit("/session/update", {
    data,
    method: "PUT",
    headers: { "x-api-key": apiKey },
  });
}

// To do: Add signature, append receipts to claim Pdf
async function generateClaimPdf() {
  const gavelWorkflowName = selectedProvider.value?.workflow || "";

  // Generate the Pdf with Gavel
  const response = await documentStore.getPDF(sessionId, gavelWorkflowName);
  claimPdfs.value = [response.data];

  // Add the signature
  claimPdfs.value[0] = await addSignatureToPdf(
    claimPdfs.value[0],
    signatureDataUrl.value,
    signatureInfo.value
  );

  // Add the receipt
  const receiptUrl = await receipt.toDataUrl();
  if (receiptUrl) {
    claimPdfs.value[0] = await appendImageOrPdf(claimPdfs.value[0], receiptUrl);
  }

  claimPdfs.value[0] = await flattenPDFForm(claimPdfs.value[0]);

  const pdfName = sessionId + "-claim.pdf";
  const file = await base64ToFile(
    claimPdfs.value[0],
    "application/pdf",
    pdfName
  );
  await useBucketStore()
    .saveFile(pdfName, file, "application/pdf")
    .then(() => {
      claimPdfKey.value = pdfName;
      errorGeneratingPdfs.value = false;
    })
    .catch(() => {
      // To do: handle errors
      errorGeneratingPdfs.value = true;
    });
}

async function submitClaim() {
  await documentStore.submitDocument(claimPdfs.value[0], sessionId);
}

const fullName = computed((): string => {
  const { lastName, firstName, middleInitial } = variables.value;
  return `${firstName} ${middleInitial ? `${middleInitial}. ` : ""}${lastName}`;
});

const accountHolderFullName = computed((): string => {
  const lastName = variables.value.accountHolderLastName;
  const firstName = variables.value.accountHolderFirstName;
  const middleInitial = variables.value.accountHolderMiddleInitial;
  return `${firstName} ${middleInitial ? `${middleInitial}. ` : ""}${lastName}`;
});

const signatureDataUrl = ref<string>("");
const signatureInfo = computed(() => selectedProvider.value?.signature);
const today = new Date();
const emptyItem = {
  date: variables.value.serviceDate || today.toLocaleDateString("en-US"),
  amount: 0.0,
};

const claimItems = ref<VisionClaimItems>({
  exam: [{ ...emptyItem }],
  frames: [{ ...emptyItem }],
  singleVision: [{ ...emptyItem }],
  bifocal: [{ ...emptyItem }],
  trifocal: [{ ...emptyItem }],
  lenticular: [{ ...emptyItem }],
  progressive: [{ ...emptyItem }],
  coating: [{ ...emptyItem }],
  tint: [{ ...emptyItem }],
  otherLensOption: [{ ...emptyItem }],
  antiReflective: [{ ...emptyItem }],
  polycarbonate: [{ ...emptyItem }],
  scratchResistant: [{ ...emptyItem }],
  UV: [{ ...emptyItem }],
  rollAndPolish: [{ ...emptyItem }],
  contacts: [{ ...emptyItem }],
  contactFitting: [{ ...emptyItem }],
  otherLenses: [{ ...emptyItem }],
});

function getItemsTotal(items: VisionItem[] = []) {
  return items.reduce((sum, item) => sum + (item.amount || 0), 0);
}

function getItemsData() {
  return {
    examAmount: getItemsTotal(claimItems.value.exam),
    framesAmount: getItemsTotal(claimItems.value.frames),
    singleVisionAmount: getItemsTotal(claimItems.value.singleVision),
    bifocalAmount: getItemsTotal(claimItems.value.bifocal),
    trifocalAmount: getItemsTotal(claimItems.value.trifocal),
    lenticularAmount: getItemsTotal(claimItems.value.lenticular),
    progressiveAmount: getItemsTotal(claimItems.value.progressive),
    coatingAmount: getItemsTotal(claimItems.value.coating),
    tintAmount: getItemsTotal(claimItems.value.tint),
    otherLensOptionAmount: getItemsTotal(claimItems.value.otherLensOption),
    antiReflectiveAmount: getItemsTotal(claimItems.value.antiReflective),
    polycarbonateAmount: getItemsTotal(claimItems.value.polycarbonate),
    scratchResistantAmount: getItemsTotal(claimItems.value.scratchResistant),
    UVAmount: getItemsTotal(claimItems.value.UV),
    rollAndPolishAmount: getItemsTotal(claimItems.value.rollAndPolish),
    contactsAmount: getItemsTotal(claimItems.value.contacts),
    contactFittingAmount: getItemsTotal(claimItems.value.contactFitting),
    otherLensesAmount: getItemsTotal(claimItems.value.otherLenses),
  };
}

function getEarliestDate() {
  return Object.keys(claimItems.value).reduce((prev, item) => {
    const dateString =
      claimItems.value[item as keyof typeof claimItems.value]?.[0]?.date;
    if (dateString) {
      const dateNum = new Date(dateString).getTime();
      if (!prev || dateNum < prev.dateNum) {
        return { dateNum, dateString };
      }
    }
    return prev;
  }, null as { dateNum: number; dateString: string } | null);
}

onMounted(async () => {
  providers.value = (await useProviderStore().listVisionProviders()).data;

  if (USE_VGS) {
    vgsForm.value = new VgsForm();
    vgsForm.value.toRaw().field("#example", { name: "example", type: "text" });
  }
  await loadSession();

  if (!sessionVal.value?.hasPassedOTP) {
    useSessionStore().updateHasPassedOTP(sessionId);
  }

  window.addEventListener("resize", handleResize);

  const handleEvent = () => {
    useSessionStore().updateLastActivity(sessionId);
  };
  window.addEventListener("input", handleEvent);
  window.addEventListener("focus", handleEvent);
});

function calculateWidth() {
  // Check if window is defined
  // Access window.innerWidth directly
  const screenWidth = ref(window.innerWidth);

  if (screenWidth.value <= 500) {
    return "95%";
  } else if (screenWidth.value <= 700) {
    return "85%";
  } else {
    return "60%";
  }
}
onBeforeUnmount(() => {
  // Remove event listener on component unmount
  window.removeEventListener("resize", handleResize);
});

function handleResize() {
  // Handle the resize event here, you can call your method
  sheetWidth.value = calculateWidth();
}

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_window_item = _resolveComponent("v-window-item")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_sheet = _resolveComponent("v-sheet")!
  const _component_v_snackbar = _resolveComponent("v-snackbar")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _hoisted_1,
    _createVNode(BinkeyLogo),
    (loadingSession.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_component_v_progress_circular, {
            size: 70,
            width: 7,
            color: "primary",
            indeterminate: ""
          })
        ]))
      : (_openBlock(), _createBlock(_component_v_sheet, {
          key: 1,
          class: "mx-auto my-4",
          width: sheetWidth.value,
          color: "#f0f0f0"
        }, {
          default: _withCtx(() => [
            _createVNode(ProgressBar, {
              modelValue: currentStep.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((currentStep).value = $event))
            }, null, 8, ["modelValue"]),
            _createVNode(_component_v_card, { class: "card-container" }, {
              default: _withCtx(() => [
                _createVNode(_unref(VWindow), {
                  class: "window-container",
                  ref_key: "windowScreen",
                  ref: windowScreen,
                  modelValue: currentStep.value,
                  "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((currentStep).value = $event))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_window_item, {
                      value: _unref(VisionFormSteps).PersonalInformation
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(VForm), {
                          ref_key: "personalInformation",
                          ref: personalInformation
                        }, {
                          default: _withCtx(() => [
                            _createVNode(PatientInformationForm, {
                              modelValue: variables.value,
                              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((variables).value = $event))
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        }, 512)
                      ]),
                      _: 1
                    }, 8, ["value"]),
                    _createVNode(_component_v_window_item, {
                      value: _unref(VisionFormSteps).InsuranceInformation
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(VForm), {
                          ref_key: "insuranceInformation",
                          ref: insuranceInformation
                        }, {
                          default: _withCtx(() => [
                            _createVNode(InsuranceInformationForm, {
                              providers: providers.value,
                              modelValue: variables.value,
                              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((variables).value = $event)),
                              onInsuranceStatusChanged: updateInsuranceStatus
                            }, null, 8, ["providers", "modelValue"])
                          ]),
                          _: 1
                        }, 512)
                      ]),
                      _: 1
                    }, 8, ["value"]),
                    _createVNode(_component_v_window_item, {
                      value: _unref(VisionFormSteps).ClaimInformation
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(VForm), {
                          ref_key: "claimInformation",
                          ref: claimInformation
                        }, {
                          default: _withCtx(() => [
                            _createVNode(ClaimInformationForm, {
                              modelValue: variables.value,
                              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((variables).value = $event)),
                              claimItems: claimItems.value,
                              "onUpdate:claimItems": _cache[4] || (_cache[4] = ($event: any) => ((claimItems).value = $event)),
                              "validate-method": validateCurrentStep,
                              providers: providers.value
                            }, null, 8, ["modelValue", "claimItems", "providers"]),
                            _hoisted_3,
                            (receiptLoaded.value)
                              ? (_openBlock(), _createBlock(FileInput, {
                                  key: 0,
                                  file: _unref(receipt).getFile(),
                                  "onUpdate:file": _cache[5] || (_cache[5] = ($event: any) => (_unref(receipt).setFile($event))),
                                  rules: [rules.requiredRule],
                                  fileMBLimitSize: 10,
                                  required: ""
                                }, null, 8, ["file", "rules"]))
                              : _createCommentVNode("", true)
                          ]),
                          _: 1
                        }, 512)
                      ]),
                      _: 1
                    }, 8, ["value"]),
                    _createVNode(_component_v_window_item, {
                      value: _unref(VisionFormSteps).Review
                    }, {
                      default: _withCtx(() => [
                        _createVNode(Review, {
                          modelValue: currentStep.value,
                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((currentStep).value = $event)),
                          claimItems: claimItems.value,
                          "onUpdate:claimItems": _cache[7] || (_cache[7] = ($event: any) => ((claimItems).value = $event)),
                          variables: variables.value
                        }, null, 8, ["modelValue", "claimItems", "variables"])
                      ]),
                      _: 1
                    }, 8, ["value"]),
                    _createVNode(_component_v_window_item, {
                      value: _unref(VisionFormSteps).FinalReview
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(VForm), {
                          ref_key: "finalReviewForm",
                          ref: finalReviewForm
                        }, {
                          default: _withCtx(() => [
                            _createVNode(FinalReview, {
                              modelValue: signatureDataUrl.value,
                              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((signatureDataUrl).value = $event)),
                              claimPdfs: claimPdfs.value,
                              loading: generatingPdfs.value,
                              showPdfs: SHOW_CLAIM_PDFS,
                              errorGeneratingPdfs: errorGeneratingPdfs.value
                            }, null, 8, ["modelValue", "claimPdfs", "loading", "errorGeneratingPdfs"])
                          ]),
                          _: 1
                        }, 512)
                      ]),
                      _: 1
                    }, 8, ["value"]),
                    _createVNode(_component_v_window_item, {
                      value: _unref(VisionFormSteps).Confirmation
                    }, {
                      default: _withCtx(() => [
                        _createVNode(ClaimFiled)
                      ]),
                      _: 1
                    }, 8, ["value"])
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _: 1
            }),
            (currentStep.value != _unref(VisionFormSteps).Confirmation)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  (shouldShowBackBtn.value)
                    ? (_openBlock(), _createBlock(_component_v_btn, {
                        key: 0,
                        class: "binkey-button-back",
                        onClick: prevStep,
                        disabled: savingSession.value || generatingPdfs.value
                      }, {
                        default: _withCtx(() => [
                          _hoisted_5,
                          _createTextVNode(" Back ")
                        ]),
                        _: 1
                      }, 8, ["disabled"]))
                    : _createCommentVNode("", true),
                  (shouldShowNextButton.value)
                    ? (_openBlock(), _createBlock(_component_v_btn, {
                        key: 1,
                        class: "binkey-button-next",
                        onClick: onClickNext,
                        color: "primary",
                        disabled: 
            (variables.value.visionPlanType != undefined &&
              variables.value.visionPlanType != 'PPO' &&
              currentStep.value == _unref(VisionFormSteps).InsuranceInformation) ||
            savingSession.value ||
            generatingPdfs.value
          
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", {
                            class: _normalizeClass(`next-btn-text-container ${
              currentStep.value == _unref(VisionFormSteps).FinalReview
                ? 'submit-claim-text'
                : ''
            }`)
                          }, [
                            _createElementVNode("div", null, _toDisplayString(currentStep.value == _unref(VisionFormSteps).FinalReview
                  ? "Submit Claim"
                  : "Next"), 1),
                            (currentStep.value != _unref(VisionFormSteps).FinalReview)
                              ? (_openBlock(), _createElementBlock("img", _hoisted_6))
                              : _createCommentVNode("", true)
                          ], 2)
                        ]),
                        _: 1
                      }, 8, ["disabled"]))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["width"])),
    _createVNode(_component_v_snackbar, {
      modelValue: shouldShowErrorMessage.value,
      "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((shouldShowErrorMessage).value = $event)),
      color: "red",
      timeout: "2000"
    }, {
      actions: _withCtx(() => [
        _createVNode(_component_v_btn, {
          color: "white",
          variant: "text",
          onClick: _cache[10] || (_cache[10] = ($event: any) => (shouldShowErrorMessage.value = false))
        }, {
          default: _withCtx(() => [
            _createTextVNode(" X ")
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(errorMessage.value) + " ", 1)
      ]),
      _: 1
    }, 8, ["modelValue"])
  ], 64))
}
}

})